/**
 * This page lists all jobs within a project.
 */
import {WHITE} from '@wandb/weave/common/css/globals.styles';
import React, {useEffect, useState} from 'react';

import {
  useFetchProjectJobsQuery,
  useFetchRecentlyUsedJobsQuery,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {BannerBoldText} from '../../Banner';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {useFetchProjectJobsIncrementally, usePublicQueueAccess} from '../hooks';
import {JobDrawer} from '../JobDrawer/JobDrawer';
import {WANDB_ENTITY_NAME, WANDB_JOBS_PROJECT_NAME} from '../utils';
import {CloseableBanner} from './CloseableBanner';
import {JobsHeader} from './JobsHeader';
import * as S from './JobsPage.styles';
import {JobsPageTab, JobsPageTabs} from './JobsPageTabs';
import {JobsTable} from './JobsTable';
import {getJobInfo, getPublicJobInfo, getRecentlyUsedJobInfo} from './jobUtil';
import {ZeroJobs} from './ZeroJobs';

interface JobsPageProps {
  entityName: string;
  projectName: string;
}

export const JobsPage: React.FC<JobsPageProps> = ({
  entityName,
  projectName,
}) => {
  const [jobToRun, setJobToRun] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<JobsPageTab>();

  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();

  const publicJobsQuery = useFetchProjectJobsQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      projectName: WANDB_JOBS_PROJECT_NAME,
    },
    skip:
      !hasPublicQueueAccess ||
      (entityName === WANDB_ENTITY_NAME &&
        projectName === WANDB_JOBS_PROJECT_NAME),
  });

  const recentlyUsedJobsQuery = useFetchRecentlyUsedJobsQuery({
    variables: {
      entityName,
      projectName,
    },
  });

  const projectJobsQuery = useFetchProjectJobsIncrementally(
    entityName,
    projectName
  );

  // Get data first to determine available tabs
  const publicJobEdges: any[] =
    publicJobsQuery.data?.project?.artifactType?.artifactCollections?.edges ??
    [];
  const jobEdges: any[] =
    projectJobsQuery.data?.project?.artifactType?.artifactCollections?.edges ??
    [];
  const recentlyUsedJobRunEdges =
    recentlyUsedJobsQuery.data?.project?.runs?.edges ?? [];

  const projectJobs = getJobInfo(jobEdges, entityName, projectName);
  const publicJobs = getPublicJobInfo(
    publicJobEdges,
    WANDB_ENTITY_NAME,
    WANDB_JOBS_PROJECT_NAME
  );
  const recentlyUsedJobs = getRecentlyUsedJobInfo(
    recentlyUsedJobRunEdges as any
  );

  const hasPublicJobs = !!(hasPublicQueueAccess && publicJobs.length > 0);
  const hasProjectJobs = projectJobs.length > 0;
  const hasRecentlyUsedJobs = recentlyUsedJobs.length > 0;

  // set initial tab based on available options
  useEffect(() => {
    if (currentTab) {
      return;
    }

    if (hasProjectJobs) {
      setCurrentTab(JobsPageTab.PROJECT);
    } else if (hasPublicJobs) {
      setCurrentTab(JobsPageTab.PUBLIC);
    } else if (hasRecentlyUsedJobs) {
      setCurrentTab(JobsPageTab.RECENTLY_USED);
    }
  }, [hasProjectJobs, hasPublicJobs, hasRecentlyUsedJobs, currentTab]);

  if (
    projectJobsQuery.loading ||
    publicJobsQuery.loading ||
    recentlyUsedJobsQuery.loading ||
    isFeatureFlagLoading
  ) {
    return <Loader active name="jobs-page-loader" />;
  }

  if (!hasProjectJobs && !hasRecentlyUsedJobs) {
    // Note: BlankPage component does not match new UI style.
    return (
      <S.Content style={{backgroundColor: WHITE, height: '100%'}}>
        <JobsHeader />
        <ZeroJobs />
      </S.Content>
    );
  }

  const onRun = (jobId: string) => {
    setJobToRun(jobId);
  };
  const closeDrawer = () => {
    setJobToRun(null);
    Analytics.track('jobs drawer closed', {
      entityName,
    });
  };

  const onJobDelete = () => {
    projectJobsQuery.refetch();
  };

  const renderTabContent = () => {
    if (currentTab === JobsPageTab.PUBLIC) {
      return (
        <JobsTable
          jobs={publicJobs}
          entityName={entityName}
          projectName={projectName}
          onRun={onRun}
          onJobDelete={onJobDelete}
        />
      );
    } else if (currentTab === JobsPageTab.PROJECT) {
      return (
        <JobsTable
          jobs={projectJobs}
          entityName={entityName}
          projectName={projectName}
          onRun={onRun}
          onJobDelete={onJobDelete}
        />
      );
    } else if (currentTab === JobsPageTab.RECENTLY_USED) {
      return (
        <JobsTable
          jobs={recentlyUsedJobs}
          entityName={entityName}
          projectName={projectName}
          onRun={onRun}
          onJobDelete={onJobDelete}
        />
      );
    }

    return <></>;
  };

  return (
    <S.Content>
      <JobsHeader />
      <CloseableBanner hintKey="jobs">
        <BannerBoldText>Run your first job!</BannerBoldText>
        Jobs are reusable run templates that allow you and your team to quickly
        execute runs at scale, locally or on extra GPUs. To run a job, select a
        job and click "Launch".
      </CloseableBanner>

      {/* Only show tabs if there are jobs in either category */}
      {currentTab &&
        (hasProjectJobs || hasPublicJobs || hasRecentlyUsedJobs) && (
          <JobsPageTabs
            tab={currentTab}
            hasPublicJobs={hasPublicJobs}
            hasProjectJobs={hasProjectJobs}
            hasRecentlyUsedJobs={hasRecentlyUsedJobs}
            onTabChange={(_, tabValue: JobsPageTab) => setCurrentTab(tabValue)}
          />
        )}

      {renderTabContent()}

      <JobDrawer
        open={jobToRun != null}
        closeDrawer={closeDrawer}
        entityName={entityName}
        projectName={projectName}
        defaultJobId={jobToRun ?? undefined}
        defaultJobVersion="latest"
      />
    </S.Content>
  );
};
